import { GalleryVerticalEnd } from "lucide-react";
import { FolderIcon, Tags } from "lucide-react";
import { useMemo } from "react";

import { useIsItemMoveDragging, useSidebarData } from "@/modules/mine";
import type { FolderData, TagData } from "@/modules/mine/types";
import { useRouteInfo } from "@/shared/hooks/use-route-info";
import {
  SidebarGroup,
  Sidebar,
  SidebarContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from "@/shared/ui/sidebar";
import { SidebarPluginsEntry } from "@/widgets/app-sidebar/sidebar-plugins";

import {
  MAIN_NAV_ITEMS,
  SECONDARY_NAV_ITEMS,
} from "./config";
import SidebarFolders from "./sidebar-folders";
import SidebarPrimary from "./sidebar-primary";
import { SidebarResizeHandle } from "./sidebar-resize";
import SidebarSecondary from "./sidebar-secondary";
import SidebarTags from "./sidebar-tags";
import SidebarUser from "./sidebar-user";
import type { Folder } from "./types";

export { ResizableSidebarProvider } from "./sidebar-resize";

function convertFolderData(folderData: FolderData, parentPrivate = false): Folder {
  const isPrivate = parentPrivate || folderData.privacy_enabled;
  return {
    id: String(folderData.id),
    name: folderData.name,
    note: folderData.note,
    isEncrypted: folderData.privacy_enabled,
    isPrivate,
    icon: FolderIcon,
    children:
      folderData.sub_folders?.length > 0
        ? folderData.sub_folders.map((child) => convertFolderData(child, isPrivate))
        : undefined,
  };
}

function convertTagData(tag: TagData) {
  return {
    id: String(tag.id),
    name: tag.name,
    icon: Tags,
  };
}

export function AppSidebar() {
  const { folders: foldersData, tags: tagsData } = useSidebarData();
  const isItemMoveDragging = useIsItemMoveDragging();

  // 获取路由信息
  const routeInfo = useRouteInfo();

  // 转换文件夹数据
  const folders = useMemo(() => {
    return foldersData.map((folder) => convertFolderData(folder));
  }, [foldersData]);

  // 转换标签数据
  const tags = useMemo(() => {
    return tagsData.map(convertTagData);
  }, [tagsData]);

  return (
    <Sidebar collapsible="icon">
      {/* 侧边栏头部 - Logo 和应用信息 */}
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <a href="/">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  <GalleryVerticalEnd className="size-4" />
                </div>
                <div className="flex flex-col gap-0.5 leading-none">
                  <span className="font-semibold">Pinbox</span>
                  <span className="">v5.0.0</span>
                </div>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      {/* 侧边栏主内容区 */}
      <SidebarContent>
        {/* 主导航 - 未分类、全部收藏、发现等 */}
        <SidebarPrimary items={MAIN_NAV_ITEMS} routeInfo={routeInfo} />

        {/* 中间区域 - 文件夹和标签列表 */}
        {/* 保持 flex-1 撑开空间，内容在收起时隐藏 */}
        <SidebarGroup className="flex-1">
          <div
            className={`group-data-[collapsible=icon]:hidden ${
              isItemMoveDragging
                ? "relative rounded-lg after:pointer-events-none after:absolute after:inset-0 after:z-50 after:animate-pulse after:rounded-lg after:border-2 after:border-dashed after:border-primary/30 motion-reduce:after:animate-none"
                : ""
            }`}
          >
            <SidebarFolders items={folders} routeInfo={routeInfo} />
            <SidebarTags items={tags} routeInfo={routeInfo} />
          </div>
        </SidebarGroup>

        {/* 插件页面入口（route 集成点） */}
        <SidebarPluginsEntry />

        {/* 次要导航 - 回收站、设置、帮助等 */}
        <SidebarSecondary items={SECONDARY_NAV_ITEMS} routeInfo={routeInfo} />
      </SidebarContent>

      {/* 侧边栏底部 - 用户信息 */}
      <SidebarFooter>
        <SidebarUser />
      </SidebarFooter>

      <SidebarResizeHandle />
    </Sidebar>
  );
}
